import { type IntlService, t } from 'ember-intl'
import { mapValues } from 'lodash'
import { trackedFunction } from 'reactiveweb/function'
import Component from '@glimmer/component'
import { service } from '@ember/service'
import type { SubscriptionType } from 'district-ui-client/domain/subscription-type'
import { join, joinQueryParams } from 'district-ui-client/utils/uri'
import config from 'district-ui-client/config/environment'
import type AuthToken from '@blakeelearning/auth/services/auth-token'
import type { Log } from '@blakeelearning/log'

export interface SubscriptionSummary {
  school_count: number
  teacher_count: number
  student_count: number
  licence_count: number
}

interface SubscriptionSummaryHeaderSignature {
  Element: HTMLDivElement
  Args: {
    districtId: string
    subscriptionType: SubscriptionType
  }
}

export class SubscriptionSummaryHeader extends Component<SubscriptionSummaryHeaderSignature> {
  @service intl!: IntlService

  @service authToken!: AuthToken

  @service log!: Log

  subscriptionSummary = trackedFunction(this, async () => {
    const { districtId, subscriptionType } = this.args
    const summary: SubscriptionSummary = (await this.fetchSubscriptionSummary(districtId, subscriptionType)) || {
      school_count: 0,
      teacher_count: 0,
      student_count: 0,
      licence_count: 0,
    }

    return mapValues(summary, (value) => this.intl.formatNumber(value))
  })

  fetchSubscriptionSummary = async (districtId: string, subscriptionType: SubscriptionType) => {
    const url = this.buildUrl('district', districtId, 'subscription-summary')
    const query = { filter: { subscriptionType: [subscriptionType] } }

    const response = await fetch(joinQueryParams(url, query), {
      method: 'GET',
      headers: {
        Authorization: this.authToken.token || '',
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })

    return this.deserializeSummaryData(response, districtId, subscriptionType)
  }

  private async deserializeSummaryData(
    response: Response,
    districtId: string,
    subscriptionType: SubscriptionType,
  ): Promise<SubscriptionSummary | null> {
    if (!response.ok) {
      return null
    }

    try {
      const result = (await response.json()) as unknown as Record<SubscriptionType, SubscriptionSummary>
      return result[subscriptionType] || null
    } catch (error) {
      this.log.error(`Error parsing subscription summary response`, {
        districtId,
        subscriptionType,
        error: JSON.stringify(error),
      })
      return null
    }
  }

  private buildUrl(scope: 'district', id: string, statisticsType: 'subscription-summary'): string {
    return join(config.gravityV3Url, 'district-ui/statistics', scope, id, statisticsType).href
  }

  <template>
    <div
      data-test-stats-header
      {{! fade in when data available, instantly disappear when running (eg switching subtype) }}
      class="flex items-center gap-x-3 text-sm font-semibold uppercase text-neutral-250 transition-opacity
        {{if this.subscriptionSummary.isResolved 'visible opacity-100' 'invisible opacity-0'}}"
      ...attributes
    >
      <span>{{t "schools"}}: {{this.subscriptionSummary.value.school_count}}</span>
      <span class="h-3 border-x border-neutral-200" />
      <span>{{t "teachers"}}: {{this.subscriptionSummary.value.teacher_count}}</span>
      <span class="h-3 border-x border-neutral-200" />
      <span>{{t "students"}}: {{this.subscriptionSummary.value.student_count}}</span>
      <span class="h-3 border-x border-neutral-200" />
      <span>{{t "components.statsHeader.availableLicences"}}:
        {{this.subscriptionSummary.value.licence_count}}
      </span>
    </div>
  </template>
}
